import { CircularRelationshipError, DuplicateEmployeeError } from '@employees/errors';
import { Employee } from '@models/employee/employee.model';

function employeeDoesNotReportToSomeoneInArray(employee: Employee, arrayOfEmployees: Employee[]): boolean {
    const someEmployeeThatThisEmployeeReportsTo = arrayOfEmployees.find((someEmployee) =>
        employee.reportsToEmployee(someEmployee)
    );
    return !someEmployeeThatThisEmployeeReportsTo;
}

function getEmployeesWhoDoNotReportToOtherEmployeesInArray(employees: Employee[]): Employee[] {
    return employees.filter(
        (employee, _index, arrayOfEmployees) =>
            employee.doesNotReportToAnyone() || employeeDoesNotReportToSomeoneInArray(employee, arrayOfEmployees)
    );
}
/**
 * Given a flat array of employees, create a nested structure based on reporting structure
 * @param {Employee[]} employees
 * @returns {Employee[]}
 */
export function convertToNestedReportsArray(employees: Employee[]): Employee[] {
    const isDuplicatesBefore = isDuplicates(employees);
    if (isDuplicatesBefore) {
        throw new DuplicateEmployeeError();
    }

    const employeesWhoDoNotReportToOtherEmployeesInThisArray =
        getEmployeesWhoDoNotReportToOtherEmployeesInArray(employees);

    const nestedEmployees = recurseReportsArray(
        employeesWhoDoNotReportToOtherEmployeesInThisArray,
        employees,
        employees.length
    );
    const isDuplicatesAfter = isDuplicates(nestedEmployees);
    if (isDuplicatesAfter) {
        throw new DuplicateEmployeeError();
    }

    return nestedEmployees;
}

function recurseReportsArray(employees: Employee[], allEmployees: Employee[], recursiveDepth: number): Employee[] {
    return employees
        .map((employee, _index, array) => {
            recursiveDepth--;
            // Error catching
            if (recursiveDepth < 0) {
                throw new CircularRelationshipError();
            }

            // Base case
            const reports = getReports(employee, allEmployees);

            // If no one reports to them, just return them
            if (!reports.length) {
                employee.reports = [];
                return employee;
            }

            // if people report to them, go a level deeper and then attach the reports
            employee.reports = recurseReportsArray(reports, allEmployees, recursiveDepth);
            recursiveDepth++;

            // Deduplicate
            const reportsList = flattenReportingChain(employee);

            reportsList.forEach((report) => {
                const indexInArray = array.findIndex((item) => item.id === report.id);
                if (indexInArray === -1) {
                    return;
                }
                array.splice(indexInArray, 1);
            });

            return employee;
        })
        .filter((employee) => employee)
        .sort((a, b) => {
            if (a.reports.length < b.reports.length) {
                return 1;
            }
            return -1;
        });
}

/**
 * Get an array of all employees in the company that report to a user
 * @param {Employee} supervisor
 * @param allEmployees
 * @returns {Employee[]}
 */
function getReports(supervisor: Employee, allEmployees: Employee[]): Employee[] {
    return allEmployees.filter((employee) => employee.reportsTo === supervisor.id);
}

/**
 * Given an employee, return a flat array consisting of all of the employees below them
 * @param {Employee} employee
 * @param isInitialCall boolean
 * @returns {Employee[]}
 */
function flattenReportingChain(employee: Employee, isInitialCall = true): Employee[] {
    if (!employee.reports || !employee.reports.length) {
        if (isInitialCall) {
            return [];
        }
        return [employee];
    }

    // if the employee has reports, get a flattened list of their direct reports and add them to it.
    const reportsList = employee.reports.reduce(
        (accumulator, reportsTo) => accumulator.concat(flattenReportingChain(reportsTo, false)),
        []
    );

    if (!isInitialCall) {
        reportsList.push(employee);
    }

    return reportsList;
}

/**
 * Check a list of employees for duplicates
 * @param {Employee[]} employees
 * @param {Employee[] | null} container
 * @returns {Boolean}
 */
function isDuplicates(employees: Employee[], container: Employee[] | null = []): boolean {
    const duplicates = employees.filter((employee) => {
        if (employee.reports && employee.reports.length) {
            const result = isDuplicates(employee.reports, container);
            if (result) {
                return true;
            }
        }

        const containerEmployee = container.find((inContainerEmployee) => inContainerEmployee.id === employee.id);

        if (containerEmployee) {
            return true;
        }

        container.push(employee);
        return false;
    });

    return Boolean(duplicates.length);
}
